// Ticket Evolution event dates (`occurs_at_local`, and our stored order `eventDate`) are the
// venue's WALL-CLOCK time with the venue's UTC offset appended (e.g. "2026-11-08T13:05:00-08:00"
// is a 1:05 PM kickoff in Santa Clara). Parsing that into an instant and formatting it in
// America/New_York shifted every non-Eastern event (Las Vegas -3h, Europe +5h...) and could land a
// late event on the wrong calendar day, which then fed wrong flight/hotel dates.
// These helpers read the wall-clock fields exactly as written and never convert time zones.
const WALL_CLOCK_RE =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/;

/**
 * Wall-clock fields of an ISO-like string, ignoring any offset/"Z". Returned as a Date whose UTC
 * fields hold the wall clock (format it with timeZone: 'UTC'). Null if the string isn't ISO-like.
 */
export const parseWallClock = (date?: string | null): Date | null => {
  if (!date || typeof date !== 'string') return null;
  const match = WALL_CLOCK_RE.exec(date.trim());
  if (!match) return null;
  const [, year, month, day, hour = '0', minute = '0', second = '0'] = match;
  const parsed = new Date(
    Date.UTC(+year, +month - 1, +day, +hour, +minute, +second)
  );
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** Wall-clock Date when the string is ISO-like (format in UTC), else the legacy instant (format in ET). */
const toEventInstant = (date: string) => {
  const wall = parseWallClock(date);
  return wall
    ? { value: wall, timeZone: 'UTC' as const }
    : {
        value: new Date(Date.parse(date)),
        timeZone: 'America/New_York' as const,
      };
};

export const getInternationalDateTz = (date: string) => {
  if (!date) return '';
  const { value, timeZone } = toEventInstant(date);
  return new Intl.DateTimeFormat('en-US', { timeZone }).format(value);
};

export const getInternationalYear = (date: string) => {
  return date
    ? new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
      }).format(Date.parse(date))
    : '';
};

export const getInternationalDMY = (date: string, timeZone?: string) => {
  return date
    ? new Intl.DateTimeFormat('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        timeZone,
      }).format(Date.parse(date))
    : '';
};

export const getInternationalFullDate = (
  date: string,
  year: any = 'numeric'
) => {
  if (!date) return '';
  const { value, timeZone } = toEventInstant(date);
  return new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    month: 'short',
    weekday: 'short',
    year,
    timeZone,
  }).format(value);
};

// `hasTimezone` is kept for call-site compatibility: event strings are always read as venue
// wall-clock time now (see parseWallClock); it only matters for non-ISO input.
export const getInternationalTime = (
  date: string,
  hasTimezone: boolean = false
) => {
  if (!date) return '';
  const wall = parseWallClock(date);
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    timeZone: wall || !hasTimezone ? 'UTC' : 'America/New_York',
  }).format(wall ?? Date.parse(date));
};

export const getInternationalWeekDateTz = (
  date: string,
  hasWeekday: boolean = true
) => {
  if (!date) return '';
  const { value, timeZone } = toEventInstant(date);
  return new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    month: 'short',
    weekday: hasWeekday ? 'short' : undefined,
    timeZone,
  }).format(value);
};

export const getInternationalTimeShort = (date: string) => {
  return date
    ? new Intl.DateTimeFormat('en-US', {
        day: '2-digit',
        month: 'short',
        weekday: 'short',
        timeZone: 'UTC',
      }).format(Date.parse(date))
    : '';
};

export const getInternationalFullDateDynamic = (
  date: string,
  weekday: any = 'short',
  year: any = 'numeric',
  hasTimezone: boolean = false
) => {
  if (!date) return '';
  const wall = parseWallClock(date);
  return new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    month: 'short',
    weekday,
    year,
    timeZone: wall || !hasTimezone ? 'UTC' : 'America/New_York',
  }).format(wall ?? Date.parse(date));
};

export const isToday = (timestamp: string) => {
  const today = new Date();
  const theDate = new Date(parseInt(timestamp, 10));

  return (
    today.getFullYear() === theDate.getFullYear() &&
    today.getMonth() === theDate.getMonth() &&
    today.getDate() === theDate.getDate()
  );
};
